import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import usePageMeta from '../hooks/usePageMeta';

const H2 = 'font-display text-2xl font-bold uppercase tracking-tight text-white';
const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    paragraphs: ['We may collect the following information, depending on how you interact with us:'],
    bullets: [
      'Contact and professional information, such as your name, business email address, telephone number, company, job title and business address.',
      'Inquiry and project information, such as property types, property locations, numbers of units or appliances, appliance makes and models, product requirements, purchasing plans and installation needs.',
      'Correspondence and submitted materials, including messages, photographs, documents and other information you provide when requesting a consultation, product information, compatibility assessment, quotation or pilot program.',
      'Technical and usage information, such as your IP address, browser and device information, referring pages, pages visited and the dates and times of your visits, collected through server logs and any cookies or similar technologies described below.',
    ],
    after: [
      'Please provide only information relevant to your business inquiry. Do not submit residents’ or tenants’ personal information, medical information, payment card details or other sensitive personal information through our general inquiry forms. Before providing someone else’s personal information, please ensure you are authorized to do so.',
    ],
  },
  {
    title: '2. How We Use Information',
    paragraphs: ['We use personal information as appropriate to:'],
    bullets: [
      'Respond to inquiries and communicate with you.',
      'Assess product compatibility and commercial requirements.',
      'Prepare quotations, proposals, pilot programs and supporting business materials.',
      'Coordinate purchasing, delivery, installation arrangements and customer support where applicable.',
      'Maintain business relationships and records.',
      'Send relevant product or commercial program updates, where permitted by law and subject to your communication preferences.',
      'Operate, maintain, secure and improve our website and services.',
      'Prevent misuse, investigate security incidents, meet legal obligations and protect legal rights.',
    ],
  },
  {
    title: '3. How We Disclose Information',
    paragraphs: ['We may disclose personal information to:'],
    bullets: [
      'Service providers that support our operations, such as website hosting, data storage, email, inquiry management and technical support providers.',
      'Fulfillment or project partners, where necessary to address your request or carry out an agreed order or project.',
      'Professional advisers, including legal, accounting and insurance advisers, where necessary for their services.',
      'Authorities or other parties, when required by law or reasonably necessary to protect rights, safety or security.',
      'Parties to a business transaction, in connection with a proposed or completed merger, acquisition, financing, reorganization or sale of all or part of our business, subject to applicable protections.',
      'Other recipients at your direction or with your consent.',
    ],
    after: ['RangeSafe does not sell personal information.'],
  },
  {
    title: '4. Marketing Choices',
    paragraphs: [
      'You may opt out of marketing emails by using the unsubscribe link in those emails or contacting us at privacy@rangesafe.com.',
      'Opting out of marketing does not prevent us from sending communications needed to respond to your inquiries, support an order or manage an existing business relationship.',
    ],
  },
  {
    title: '5. Information Retention',
    paragraphs: [
      'We retain personal information for as long as reasonably necessary for the purposes described in this policy, including responding to inquiries, managing commercial relationships, maintaining required records, resolving disputes and meeting legal obligations.',
      'Retention periods depend on the nature of the information, the purpose for which it was collected and applicable requirements. We may retain limited information needed to honor an unsubscribe or other privacy preference.',
    ],
  },
  {
    title: '6. Information Security',
    paragraphs: [
      'We use reasonable administrative, technical and organizational safeguards designed to protect personal information. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    title: '7. Your Privacy Rights',
    paragraphs: [
      'Depending on where you live and which laws apply, you may have rights to request access to, correction of, deletion of or a portable copy of your personal information. You may also have rights to withdraw consent or opt out of certain uses or disclosures, including targeted advertising or the sale or sharing of personal information, where applicable.',
      'To make a request, contact privacy@rangesafe.com. We may need to verify your identity and, where applicable, an authorized agent’s authority before responding. We will respond in accordance with applicable law, subject to available exceptions.',
      'Where applicable law provides a right to appeal a denied request, you may contact the same email address with the subject line “Privacy Request Appeal.” We will not discriminate against you for exercising applicable privacy rights.',
    ],
  },
  {
    title: '8. Notice for California Residents',
    paragraphs: [
      'If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the CPRA, provides specific rights regarding your personal information. In the past 12 months we have collected only the categories of personal information described in Section 1: identifiers (such as name, email address, phone number and organization), commercial information (such as the details of your inquiry) and internet activity (such as pages viewed, collected through analytics cookies only where you have consented). This information comes from you directly or from your browser and is used for the business purposes described in Section 2.',
      'We do not sell your personal information and we do not share your personal information for cross-context behavioral advertising.',
      'You may request to know, access, correct or delete your personal information, subject to applicable exceptions, and you may opt out of the sale or sharing of personal information (which we do not engage in). We will not discriminate against you for exercising these rights. To make a request, contact privacy@rangesafe.com. You may use an authorized agent, subject to verification of the agent’s authority.',
      'If your browser sends a Global Privacy Control (GPC) signal, we treat it as a request to opt out of non-essential analytics cookies on this website and analytics will remain off during your visit.',
    ],
  },
  {
    title: '9. Third-Party Websites',
    paragraphs: [
      "Our website links to RangeSafe's retail website. Privacy practices may be different for that website, and we encourage you to review those policies before providing personal information.",
    ],
  },
  {
    title: '10. Children’s Privacy',
    paragraphs: [
      'Our commercial website is intended for business and institutional audiences and is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided personal information to us, please contact us so we can investigate and take appropriate action.',
    ],
  },
  {
    title: '11. Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. The revised version will be posted on this page with an updated effective date. We will provide additional notice where required by applicable law.',
    ],
  },
];

export default function PrivacyPolicy() {
  usePageMeta(
    'Privacy Policy | RangeSafe Commercial Solutions',
    'How RangeSafe, LLC collects, uses and discloses personal information through the RangeSafe commercial website and related business communications.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Legal"
          lines={['Privacy Policy']}
          lead="RangeSafe, LLC (“RangeSafe,” “we,” “us” or “our”), respects your privacy. This Privacy Policy explains how we collect, use and disclose personal information through rangesafe-commercial.com and related commercial inquiries, consultations and business communications."
        />
        <p className={`mt-6 ${LABEL}`} data-testid="privacy-effective-date">
          RangeSafe Commercial Website Privacy Policy · Effective date: October 26, 2026
        </p>
      </header>

      <div className="mx-auto max-w-shell space-y-12 px-5 pb-24 sm:px-8">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.03, 0.15)}>
            <section className="max-w-3xl" data-testid={`privacy-section-${i + 1}`}>
              <h2 className={H2}>{s.title}</h2>
              {s.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-4 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b.slice(0, 40)} className="flex gap-4">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-accent" aria-hidden="true" />
                      <span className={BODY}>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after &&
                s.after.map((p) => (
                  <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                    {p}
                  </p>
                ))}
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section className="border border-line bg-panel/60 p-8 sm:p-10" data-testid="privacy-contact">
            <p className={LABEL}>Contact Us</p>
            <p className={`mt-4 max-w-2xl ${BODY}`}>
              For questions about this website or to submit a privacy request, contact:
            </p>
            <div className="mt-4 space-y-1 text-sm leading-relaxed text-dim">
              <p className="text-white">RangeSafe, LLC</p>
              <p>Email: privacy@rangesafe.com</p>
              <p>Mailing address: 4583 Highway 903, Bracey, Virginia 23919</p>
            </div>
          </section>
        </Reveal>
      </div>
    </main>
  );
}
